/* -------------------------------------------------------------------------
 * Casanera | Signature custom JS
 * -------------------------------------------------------------------------
 * Project-specific storefront behavior. Kept outside theme.js so theme
 * updates do not overwrite custom work.
 * ------------------------------------------------------------------------- */

(function () {
  'use strict';

  /* =======================================================================
     Auto-close the quick-buy modal after add to cart
     -----------------------------------------------------------------------
     The cross-sell quick-buy modal stays open after adding a product, with
     no feedback. product-form dispatches a bubbling "variant:add" on success.
     If that event came from inside a <quick-buy-modal>, close that modal the
     same way the X button does (Modal.hide()). Adds from non-modal forms
     (single-variant cross-sell, PDP, sticky add-to-cart) have no
     quick-buy-modal ancestor, so they are left untouched.
     ======================================================================= */
  document.addEventListener('variant:add', function (event) {
    var modal = event.target
      && typeof event.target.closest === 'function'
      && event.target.closest('quick-buy-modal');

    if (modal && typeof modal.hide === 'function') {
      modal.hide();
    }
  });

  /* =======================================================================
     Cart discount field (ported from newer Stretch / Dharug)
     -----------------------------------------------------------------------
     Lets a customer apply a discount code from the cart drawer. Clean
     reimplementation of the newer theme's web components against the cart
     runtime this theme ALREADY ships (global fetchCart cache, Shopify.routes,
     and a <cart-drawer> that re-renders on "cart:refresh").

     Flow: read current applicable codes from /cart.js, merge with the entered
     code (or drop one when removing), POST { discount } to /cart/update.js.
       - success           -> dispatch "cart:refresh" (drawer re-renders totals)
       - rejected / failure -> dispatch "cart:discount:error" (reveals banner)
     The discount is applied server-side immediately, so it carries to checkout.
     ======================================================================= */
  (function () {
    if (window.customElements.get('cart-discount-field')) {
      return;
    }

    var cartRoot = function () {
      return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    };

    async function currentApplicableCodes() {
      var res = await fetch(cartRoot() + 'cart.js', {
        headers: { Accept: 'application/json' },
      });
      var cart = await res.json();
      return (cart.discount_codes || [])
        .filter(function (d) { return d.applicable; })
        .map(function (d) { return d.code.toLowerCase(); });
    }

    class AbstractCartDiscount extends HTMLElement {
      async toggleDiscount(event) {
        var target = event.currentTarget;
        target.setAttribute('aria-busy', 'true');

        var codes = await currentApplicableCodes();

        // Remove button: strip its own code from the applied set
        if (target.hasAttribute('discount-code')) {
          var toRemove = target.getAttribute('discount-code').toLowerCase();
          codes = codes.filter(function (c) { return c !== toRemove; });
        }

        var entered = (event.target.value || '').trim();
        var discount = (codes.length ? codes.join(',') + ',' : '') + entered;

        var response;
        try {
          response = await fetch(cartRoot() + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
            body: JSON.stringify({ discount: discount }),
          });
        } catch (err) {
          target.setAttribute('aria-busy', 'false');
          this.dispatchEvent(new CustomEvent('cart:discount:error', { bubbles: true }));
          return;
        }

        target.setAttribute('aria-busy', 'false');

        if (!response.ok) {
          this.dispatchEvent(new CustomEvent('cart:discount:error', { bubbles: true }));
          return;
        }

        var json = await response.json();
        var rejected = (json.discount_codes || []).some(function (d) {
          return d.applicable === false;
        });

        if (rejected) {
          this.dispatchEvent(new CustomEvent('cart:discount:error', { bubbles: true }));
          return;
        }

        this.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    }

    class CartDiscountField extends AbstractCartDiscount {
      connectedCallback() {
        // "change" bubbles from the inner text input up to this element
        this.addEventListener('change', this.toggleDiscount.bind(this));
      }
    }

    class CartDiscountRemoveButton extends AbstractCartDiscount {
      connectedCallback() {
        this.addEventListener('click', this.toggleDiscount.bind(this));
      }
    }

    class CartDiscountBanner extends HTMLElement {
      connectedCallback() {
        this._reveal = function () { this.hidden = false; }.bind(this);
        document.addEventListener('cart:discount:error', this._reveal);
      }
      disconnectedCallback() {
        document.removeEventListener('cart:discount:error', this._reveal);
      }
    }

    customElements.define('cart-discount-field', CartDiscountField);
    customElements.define('cart-discount-remove-button', CartDiscountRemoveButton);
    customElements.define('cart-discount-banner', CartDiscountBanner);
  })();

  /* =======================================================================
     Random promo banner (<random-promo>)
     -----------------------------------------------------------------------
     Collection pages are full-page cached by Shopify, so a server-side
     random banner would stay frozen until the cache expires. The
     signature-promo snippet renders every valid metaobject banner as an
     inert <template> (templates never download their media). On connect we
     clone exactly ONE random template into the DOM, so only one image/video
     is ever fetched, and it reshuffles on every page load.

     The element is display:contents (set inline in Liquid), so the cloned
     .product-list__promo stays a direct grid item of <product-list> and its
     row/column span vars keep working.
     ======================================================================= */
  (function () {
    if (window.customElements.get('random-promo')) {
      return;
    }

    class RandomPromo extends HTMLElement {
      connectedCallback() {
        if (this._rendered) {
          return;
        }

        var templates = this.querySelectorAll(':scope > template');
        if (!templates.length) {
          return;
        }

        var index = Math.floor(Math.random() * templates.length);
        this.appendChild(templates[index].content.cloneNode(true));
        this._rendered = true;
      }
    }

    customElements.define('random-promo', RandomPromo);
  })();

  /* =======================================================================
     Product gallery (video-first, desktop) — nav on VIDEO slides
     -----------------------------------------------------------------------
     Scoped to galleries marked .product-gallery--video-nav (product page).
     The theme both hides the custom cursor AND disables click paging whenever
     the active slide is not an image (onMediaChange toggles `hidden`, and
     onCarouselClick returns early). So a gallery that lands on or returns to a
     video loses its desktop arrows and click-to-advance. Product-gallery videos
     have no controls, so on those galleries:
       1. re-show the arrow cursor while hovering a video slide, and
       2. re-enable click paging on video slides (prev/next by click x).
     Images stay handled by the theme (no double-advance); 3D models and other
     galleries (featured product, quick-buy) are untouched. Desktop pointer only.
     ======================================================================= */
  (function () {
    var FINE = 'screen and (pointer: fine)';

    function videoActive(carousel) {
      var cell = carousel && carousel.selectedCell;
      var type = cell ? cell.getAttribute('data-media-type') : null;
      return type === 'video' || type === 'external_video';
    }

    // 1. Keep the arrow cursor visible while hovering a video slide.
    document.addEventListener('pointermove', function (event) {
      if (!window.matchMedia(FINE).matches) return;
      var gallery = event.target.closest('product-gallery.product-gallery--video-nav');
      if (!gallery) return;
      var cursor = gallery.querySelector('.product-gallery__cursor');
      if (!cursor || !cursor.hasAttribute('hidden')) return;
      if (videoActive(gallery.querySelector('scroll-carousel.product-gallery__carousel'))) {
        cursor.removeAttribute('hidden');
      }
    });

    // 2. Re-enable click paging on video slides.
    document.addEventListener('click', function (event) {
      if (!window.matchMedia(FINE).matches) return;
      var gallery = event.target.closest('product-gallery.product-gallery--video-nav');
      if (!gallery) return;
      if (event.target.closest('button, a[href], open-lightbox-button')) return;

      var carousel = gallery.querySelector('scroll-carousel.product-gallery__carousel');
      if (!carousel || !videoActive(carousel)) return;

      var offsetX = event.clientX - carousel.getBoundingClientRect().left;
      if (offsetX > carousel.clientWidth / 2) {
        if (typeof carousel.next === 'function') carousel.next();
      } else {
        if (typeof carousel.previous === 'function') carousel.previous();
      }
    });
  })();

  /* =======================================================================
     Mobile product gallery — tap image to open zoom (lightbox)
     -----------------------------------------------------------------------
     On mobile the native zoom control is a small corner button. Make a tap
     anywhere on a gallery image open the lightbox, while a horizontal swipe
     still scrolls the carousel. A pointerdown/click distance guard tells a tap
     (open zoom) from a swipe/drag (ignored). Delegated on document so it keeps
     working after the gallery re-renders on variant change. Desktop is left to
     the theme's own per-image zoom button.
     ======================================================================= */
  (function () {
    var MOBILE = '(max-width: 999px)';
    var startX = 0;
    var startY = 0;

    document.addEventListener(
      'pointerdown',
      function (event) {
        startX = event.clientX;
        startY = event.clientY;
      },
      true
    );

    document.addEventListener('click', function (event) {
      if (!window.matchMedia(MOBILE).matches) {
        return;
      }

      var media = event.target.closest('.product-gallery__media');
      if (!media || media.dataset.mediaType !== 'image') {
        return;
      }

      // Let real controls and non-image media behave normally.
      if (event.target.closest('button, a, [role="button"], open-lightbox-button, model-viewer, video-media')) {
        return;
      }

      var gallery = media.closest('product-gallery');
      if (!gallery || !gallery.hasAttribute('allow-zoom')) {
        return;
      }

      // A swipe moves the pointer; a tap barely does.
      if (Math.hypot(event.clientX - startX, event.clientY - startY) > 10) {
        return;
      }

      // Trigger the lightbox opener that is actually visible on mobile.
      var opener = Array.prototype.slice
        .call(gallery.querySelectorAll('open-lightbox-button'))
        .find(function (button) {
          return button.offsetParent !== null;
        });
      if (!opener) {
        return;
      }

      (opener.querySelector('button') || opener).click();
    });
  })();

  /* =======================================================================
     Buunto delivery method guard (cart drawer + cart page)
     -----------------------------------------------------------------------
     The Buunto widget offers every method to every cart, while the store
     sells less, and checkout then has no rate at all (the Buunto function
     keeps only the chosen method). This hides what the order can't get:
     - below the delivery minimum, counted before discounts: Local delivery
       and Shipping, with the minimum note (the rates carry no condition,
       so this is the only place the minimum is enforced);
     - unless every product has the provincie tag (only the Livrare Externa
       shipping profile has provincie rates): Shipping, with the mixed-cart
       note when some products do have it.
     A hidden selected method switches to the first visible one.
     Works on every Buunto widget (cart drawer and cart page): each one reads
     the .signature-delivery-guard element and note templates rendered by the
     signature-delivery-guard snippet in its own cart-drawer or section.
     Inputs: data-cart-total and data-delivery-min, both in cents, and
     data-shipping (all / some / none). Methods are matched by the labels in
     Buunto's own settings, so a renamed or reordered method still maps.
     Idempotent: only writes when the state differs, so the observer never
     re-triggers itself.
     ======================================================================= */
  (function () {
    var NOTE_CLASS = 'signature-delivery-note';

    function methodLabels() {
      var settings = window.Buunto && window.Buunto.datePicker && window.Buunto.datePicker.settings;
      var messages = settings && settings.languages && settings.languages[0] && settings.languages[0].commonMessages;
      if (!messages) {
        return null;
      }
      return {
        local: messages.methodLocalDeliveryLabel,
        pickup: messages.methodStorePickupLabel,
        shipping: messages.methodShippingLabel
      };
    }

    function applyTo(picker, labels) {
      var scope = picker.closest('cart-drawer') || picker.closest('.shopify-section') || document;
      var data = scope.querySelector('.signature-delivery-guard');
      if (!data) {
        return;
      }

      var min = parseInt(data.getAttribute('data-delivery-min'), 10) || 0;
      var total = parseInt(data.getAttribute('data-cart-total'), 10) || 0;
      var shipping = data.getAttribute('data-shipping') || 'all';
      var below = min > 0 && total < min;
      var firstVisible = null;
      var selectedHidden = false;

      Array.prototype.forEach.call(picker.querySelectorAll('label'), function (option) {
        var nameEl = option.querySelector('.buunto-method-name');
        var name = nameEl ? nameEl.textContent.trim() : '';
        var hidden = (below && (name === labels.local || name === labels.shipping)) ||
          (shipping !== 'all' && name === labels.shipping);
        var display = hidden ? 'none' : '';

        if (!hidden && !firstVisible) {
          firstVisible = option;
        }
        if (option.style.display !== display) {
          option.style.display = display;
        }
        if (hidden && option.classList.contains('selected')) {
          selectedHidden = true;
        }
      });

      if (selectedHidden && firstVisible) {
        firstVisible.click();
      }

      var wanted = below ? 'min' : (shipping === 'some' ? 'mixed' : null);
      var field = picker.parentNode;
      var note = field.querySelector('.' + NOTE_CLASS);
      if (note && note.getAttribute('data-note') !== wanted) {
        note.remove();
        note = null;
      }
      var template = wanted && scope.querySelector('template[data-delivery-note="' + wanted + '"]');
      if (!note && template) {
        field.insertBefore(template.content.cloneNode(true), picker);
      }
    }

    function apply() {
      var labels = methodLabels();
      if (!labels) {
        return;
      }
      Array.prototype.forEach.call(document.querySelectorAll('.buunto-date-picker .buunto-method-picker'), function (picker) {
        applyTo(picker, labels);
      });
    }

    var scheduled = false;
    function schedule() {
      if (scheduled) {
        return;
      }
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        apply();
      });
    }

    new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
    document.addEventListener('cart:change', schedule);
  })();

  /* =======================================================================
     Buunto surcharge cleanup (cart drawer + cart page)
     -----------------------------------------------------------------------
     Surcharge lines (Livrare la Miezul Noptii) have no remove button, and
     Buunto adds or drops them only on the checkout click, so removing the
     eligible bouquet left the surcharge stuck in the cart. This removes a
     surcharge line when no other cart line is in a collection of the Buunto
     custom setting that charges it. The slot itself is left to Buunto: it
     clears __buunto_selection on every page load, so the attribute cannot
     tell whether 0:00 is still chosen. A charge with a day-specific rule or
     a non-collection filter is left alone. Inputs: data-surcharge-lines and
     data-collection-ids from the signature-delivery-guard snippet. Runs on
     load and whenever the guard data re-renders (a cart change).
     ======================================================================= */
  (function () {
    var lastSignature = null;
    var pending = null;
    var removed = {};

    function charges() {
      var settings = window.Buunto && window.Buunto.datePicker && window.Buunto.datePicker.settings;
      var list = [];
      ((settings && settings.customSettings) || []).forEach(function (custom) {
        if (custom.filterType !== 'COLLECTIONS' || !custom.enableShippingCharges) {
          return;
        }
        var collections = (custom.filterCollections || []).map(function (c) { return String(c.id); });
        (custom.shippingCharges || []).forEach(function (charge) {
          if (!charge.product) {
            return;
          }
          list.push({
            variantId: String(charge.product.variantId),
            anyDay: charge.day === 'DEFAULT',
            collections: collections
          });
        });
      });
      return list;
    }

    function guardData() {
      var found = null;
      Array.prototype.forEach.call(document.querySelectorAll('.signature-delivery-guard'), function (el) {
        if (!found && el.getAttribute('data-surcharge-lines')) {
          found = el;
        }
      });
      return found;
    }

    function check() {
      var data = guardData();
      var rules = charges();
      if (!data || !rules.length) {
        return;
      }
      var collectionIds = (data.getAttribute('data-collection-ids') || '').split(',');
      var lines = data.getAttribute('data-surcharge-lines').split(',').filter(Boolean).map(function (entry) {
        var parts = entry.split('|');
        return { key: parts[0], variantId: parts[1] };
      });

      var stale = lines.filter(function (line) {
        var rule = rules.filter(function (r) { return r.variantId === line.variantId; })[0];
        if (!rule || !rule.anyDay || removed[line.key]) {
          return false;
        }
        return !rule.collections.some(function (id) { return collectionIds.indexOf(id) !== -1; });
      });
      if (!stale.length) {
        return;
      }

      var updates = {};
      stale.forEach(function (line) {
        removed[line.key] = true;
        updates[line.key] = 0;
      });
      /* removed[] only blocks a second request while this one is in flight: a surcharge line
         keeps the same key every time it is added again, so it is forgotten once done. */
      function forget() {
        stale.forEach(function (line) {
          delete removed[line.key];
        });
      }

      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(function () {
        forget();
        if (document.querySelector('.cart__right .signature-delivery-guard')) {
          window.location.reload();
        } else {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      }).catch(forget);
    }

    function later(delay) {
      clearTimeout(pending);
      pending = setTimeout(check, delay);
    }

    /* Re-check whenever a cart re-render brings new guard data. */
    new MutationObserver(function () {
      var data = guardData();
      var signature = data ? data.getAttribute('data-surcharge-lines') + '#' + data.getAttribute('data-collection-ids') : null;
      if (signature !== lastSignature) {
        lastSignature = signature;
        if (signature) {
          later(400);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });

  })();

  /* =======================================================================
     Cart refresh after Buunto changes the cart (cart drawer + cart page)
     -----------------------------------------------------------------------
     Buunto adds or drops a surcharge (Livrare la Miezul Noptii) through the
     cart API without telling the theme, so the drawer and the cart page
     kept showing the old lines and total until a manual refresh. After a
     Buunto selection change (its BuuntoDatePicker:selectionChange event, or
     a click / change inside the widget), this polls /cart.js for up to 6 s
     and, once the total differs from the one the cart was rendered with,
     asks the drawer to re-render (cart:refresh, the theme's own event) or
     reloads the cart page. The selection survives (Buunto keeps it in the
     cart attributes). It stops as soon as the rendered total matches.
     ======================================================================= */
  (function () {
    var timer = null;

    function rendered() {
      var data = document.querySelector('.cart__right .signature-delivery-guard') ||
        document.querySelector('cart-drawer .signature-delivery-guard');
      return data ? parseInt(data.getAttribute('data-cart-total'), 10) : null;
    }

    function check(triesLeft) {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          var shown = rendered();
          if (shown !== null && cart.original_total_price !== shown) {
            if (document.querySelector('.cart__right .signature-delivery-guard')) {
              window.location.reload();
            } else {
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            }
          } else if (triesLeft > 0) {
            timer = setTimeout(function () { check(triesLeft - 1); }, 750);
          }
        })
        .catch(function () {});
    }

    function schedule() {
      if (rendered() === null) {
        return;
      }
      clearTimeout(timer);
      timer = setTimeout(function () { check(7); }, 750);
    }

    document.addEventListener('BuuntoDatePicker:selectionChange', schedule);
    document.addEventListener('change', function (event) {
      if (event.target.closest && event.target.closest('#buunto-date-picker')) {
        schedule();
      }
    });
  })();

  /* =======================================================================
     Buunto checkout guard (cart drawer + cart page)
     -----------------------------------------------------------------------
     Buunto's own check blocked an empty time slot after a page reload, but
     let it through to checkout once a product was added from the product
     page (drawer opened by the add to cart, 01/10/2026). This listens in
     the capture phase, before the theme, and asks Buunto itself:
     checkErrors() shows its errors and returns true only when a field the
     current method needs is empty (Shipping has no slot, so it passes).
     When it blocks, the cart scrolls to the field in error. It covers the
     checkout button of the cart drawer and of the cart page (the button and
     the widget share a cart-drawer or a section).
     ======================================================================= */
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[name="checkout"]');
    var scope = button && (button.closest('cart-drawer') || button.closest('.shopify-section'));
    if (!scope) {
      return;
    }

    var widget = scope.querySelector('.buunto-date-picker .buunto-method-picker');
    var picker = window.Buunto && window.Buunto.datePicker;
    if (!widget || !picker || typeof picker.checkErrors !== 'function') {
      /* The widget renders a few seconds after the page: until it shows, a click would go
         through with no date. Hold it while the widget's placeholder is there, but only for
         the first 10 s, so a widget that never loads cannot lock checkout. */
      if (scope.querySelector('#buunto-date-picker, #signature-delivery-anchor') && performance.now() < 10000) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      return;
    }
    if (!picker.checkErrors()) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    var field = scope.querySelector('.buunto-date-picker .buunto-error');
    if (field) {
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, true);

  /* =======================================================================
     Product variations carousel: edge fade + arrow state (ported from
     Unfazed 3.0, 01/10/2026)
     -----------------------------------------------------------------------
     Rows rendered by the "Layout: Carousel" setting of the Product
     variations block (.signature-variations, snippets/product-info.liquid).
     - Edge fade: a side fades only while it still hides values, and only
       when at least half a value is hidden (a row a few px too wide has
       nothing to swipe). The mask is .signature-variations.sig-variations-fade in
       signature-custom.css; without that CSS block there is no fade at all.
     - Arrows: the theme checks scrollability once, when the buttons connect,
       so a row laid out later (the quick-buy modal) kept dead arrows. Both
       buttons are recomputed from the real scroll position here. The
       scroll:edge-* listeners are added after the buttons' own, so this
       state lands last.
     New rows (quick-buy modal, section re-render) are picked up by a
     MutationObserver; size changes by a ResizeObserver and window resize.
     ======================================================================= */
  (function () {
    var SEL = '.signature-variations';
    var FADE_CLASS = 'sig-variations-fade';

    function buttonHost(row, tag) {
      return row.id ? document.querySelector(tag + '[aria-controls="' + row.id + '"]') : null;
    }

    function paintButton(host, scrollable, disabled) {
      if (!host) {
        return;
      }
      host.toggleAttribute('hidden', !scrollable);
      var button = host.querySelector('button');
      if (button) {
        button.disabled = disabled;
      }
    }

    function paint(row) {
      var max = row.scrollWidth - row.clientWidth;
      var scrollable = max > 1;
      var atStart = row.scrollLeft <= 1;
      var atEnd = row.scrollLeft >= max - 1;

      paintButton(buttonHost(row, 'carousel-prev-button'), scrollable, !scrollable || atStart);
      paintButton(buttonHost(row, 'carousel-next-button'), scrollable, !scrollable || atEnd);

      var first = row.firstElementChild;
      var fadeable = max > (first ? first.offsetWidth / 2 : 1);
      row.classList.toggle(FADE_CLASS, fadeable);
      row.style.setProperty('--sig-fade-left', fadeable && !atStart ? 'var(--sig-fade)' : '0px');
      row.style.setProperty('--sig-fade-right', fadeable && !atEnd ? 'var(--sig-fade)' : '0px');
    }

    var sizeObserver = 'ResizeObserver' in window ? new ResizeObserver(function (entries) {
      entries.forEach(function (entry) { paint(entry.target); });
    }) : null;

    function attach(row) {
      if (row.sigVariationsAttached) {
        return;
      }
      row.sigVariationsAttached = true;

      var queued = false;
      row.addEventListener('scroll', function () {
        if (queued) {
          return;
        }
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          paint(row);
        });
      }, { passive: true });
      row.addEventListener('scroll:edge-nearing', function () { paint(row); });
      row.addEventListener('scroll:edge-leaving', function () { paint(row); });

      if (sizeObserver) {
        sizeObserver.observe(row);
      }
      paint(row);
    }

    var scanQueued = false;
    function scan() {
      scanQueued = false;
      document.querySelectorAll(SEL).forEach(attach);
    }

    scan();
    new MutationObserver(function () {
      if (!scanQueued) {
        scanQueued = true;
        setTimeout(scan, 50);
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
    window.addEventListener('resize', function () {
      document.querySelectorAll(SEL).forEach(paint);
    });
  })();

})();

/* -------------------------------------------------------------------------
 * Signature Shortcuts, ported 1:1 from Reframe (reframe-demo, theme
 * "Reframe | Official Version 1.0" #156152037568) on 05/10/2026, together
 * with sections/signature-shortcuts.liquid. Two blocks, verbatim:
 * the scrolling-bar toggle (on Reframe it sits inside the file's first IIFE,
 * here it gets its own scope) and the shortcuts-only edge fade driver.
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  /* =======================================================================
     Signature Shortcuts - hide the scrolling bar when nothing overflows
     -----------------------------------------------------------------------
     The section renders its <progress-bar> whenever the layout is a carousel,
     but with few shortcuts the row fits on screen and the bar is meaningless.
     Overflow is a runtime measurement, so the bar is toggled here: hidden
     unless the list it tracks is actually wider than its visible box. Re-runs
     on resize, on load (images change the row width) and on section re-render
     in the theme editor.
     ======================================================================= */
  var SHORTCUT_SCROLLBAR_HIDDEN = 'signature-scrollbar-hidden';
  var shortcutScrollbarFrame = null;

  function updateShortcutScrollbars() {
    var bars = document.querySelectorAll('progress-bar.subcollections__scrollbar');

    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      var list = document.getElementById(bar.getAttribute('track-scroll-target'));

      if (!list) {
        continue;
      }

      bar.classList.toggle(SHORTCUT_SCROLLBAR_HIDDEN, list.scrollWidth - list.clientWidth <= 1);
    }
  }

  function queueShortcutScrollbars() {
    if (shortcutScrollbarFrame) {
      return;
    }

    shortcutScrollbarFrame = requestAnimationFrame(function () {
      shortcutScrollbarFrame = null;
      updateShortcutScrollbars();
    });
  }

  /* One-shot events measure directly; only the resize stream is coalesced through rAF, which is
     paused while the document is hidden. */
  updateShortcutScrollbars();
  document.addEventListener('DOMContentLoaded', updateShortcutScrollbars);
  window.addEventListener('load', updateShortcutScrollbars);
  document.addEventListener('shopify:section:load', updateShortcutScrollbars);
  window.addEventListener('resize', queueShortcutScrollbars);
})();

(function () {
  'use strict';

  /* =======================================================================
     Edge fade — Signature Shortcuts only
     -----------------------------------------------------------------------
     A directional fade on the shortcuts row: a side fades ONLY while it still
     hides content, so the first item is crisp at rest and the fade appears as
     you swipe. Desktop only, from 1000px up.

     SCOPE, narrowed by Sebi on 13/08/2026: this drives `.subcollections` and
     nothing else. The version ported from Unfazed on 12/08 also swept every
     native `.scroll-area` carousel and hooked the signature reels through
     Embla, which put a fade on shoppable reels, the search-drawer suggestion
     rows and every other row that overflowed on desktop. That was never the
     ask. The sweep is gone, and with it everything that existed only to make
     the sweep safe: the exclusion lists (drawers, modals, predictive search,
     the PDP gallery and its thumbnails), the allow-list for product rows
     inside those panels, the `.container`-ancestor test for full-bleed rows,
     the `sig-fade-right-only` case for sticky intro cards, the MutationObserver
     for async-injected scrollers and the `facet:update` hook.

     If the fade is ever wanted on another row, add a selector to SELECTOR.
     Do not resurrect the sweep.

     CSS holds the mask (`.sig-edge-fade` in signature-custom.css); this file
     only sets --sig-fade-left / --sig-fade-right per scroll position. The
     class is attached from JS, never from markup, so no native file needs
     re-applying after a theme update.
     ======================================================================= */
  var FADE_CLASS = 'sig-edge-fade';
  var SELECTOR = '.subcollections';
  var DESKTOP = '(min-width: 1000px)';
  var mq = window.matchMedia(DESKTOP);

  function paint(el) {
    var max = el.scrollWidth - el.clientWidth;
    var scrollable = max > 1;
    el.style.setProperty('--sig-fade-left', scrollable && el.scrollLeft > 1 ? 'var(--sig-fade)' : '0px');
    el.style.setProperty('--sig-fade-right', scrollable && el.scrollLeft < max - 1 ? 'var(--sig-fade)' : '0px');
  }

  var observer = 'ResizeObserver' in window ? new ResizeObserver(function (entries) {
    entries.forEach(function (entry) { paint(entry.target); });
  }) : null;

  function attach(el) {
    if (!el.classList.contains(FADE_CLASS)) {
      el.classList.add(FADE_CLASS);
      if (observer) observer.observe(el);
    }
    /* Repaint on every sync, not only on first attach: a resize changes what a
       row hides, and relying on ResizeObserver alone leaves the fade stale
       where that observer is unavailable or throttled (background tabs). */
    paint(el);
  }

  function detach(el) {
    el.classList.remove(FADE_CLASS);
    el.style.removeProperty('--sig-fade-left');
    el.style.removeProperty('--sig-fade-right');
    if (observer) observer.unobserve(el);
  }

  function sync(root) {
    var active = mq.matches;

    (root || document).querySelectorAll(SELECTOR).forEach(function (el) {
      if (active) {
        attach(el);
      } else {
        detach(el);
      }
    });
  }

  /* One delegated capture listener: scroll does not bubble, and this covers
     rows that arrive after first paint without re-binding anything. */
  document.addEventListener('scroll', function (event) {
    var el = event.target;
    if (el && el.nodeType === 1 && el.classList && el.classList.contains(FADE_CLASS)) {
      paint(el);
    }
  }, true);

  /* Both an `mq` listener and a `resize` listener on purpose: the in-app
     browser pane does not fire matchMedia `change` when the viewport is
     resized programmatically, so a driver with only one of the two goes stale
     under test. */
  if (mq.addEventListener) {
    mq.addEventListener('change', function () { sync(); });
  } else if (mq.addListener) {
    mq.addListener(function () { sync(); });
  }

  window.addEventListener('resize', function () { sync(); });
  window.addEventListener('load', function () { sync(); });
  document.addEventListener('shopify:section:load', function (event) { sync(event.target); });

  if (document.readyState !== 'loading') {
    sync();
  } else {
    document.addEventListener('DOMContentLoaded', function () { sync(); });
  }
})();

/* =========================================================================
 * Judge.me review widget (product page), ported from Dotare 16/10
 * -------------------------------------------------------------------------
 * 1. Mobile limit: below 700px signature-custom.css shows only the first 3
 *    review cards until the widget has .signature-jdgm-expanded. This adds the
 *    theme's outline "Vezi toate recenziile" button under the widget when there
 *    are more than 3 reviews; a click shows them all and hides the button. The
 *    button comes from <template id="SignatureJudgemeMore"> in main-product,
 *    so it is the theme's own button, translated. It sits after the widget,
 *    outside Judge.me's markup, which Judge.me re-renders on filter and sort.
 * 2. The write-review popup is appended to <body>, outside every
 *    .color-scheme, so it gets the default scheme's class (variables only).
 * 3. Judge.me's popup backdrop (shown by the CSS) closes it on click, through
 *    Judge.me's own close button, as the theme's drawers close on theirs.
 * Batched with setTimeout, not requestAnimationFrame, which never runs in a
 * hidden tab.
 * ========================================================================= */
(function () {
  var LIMIT = 3;
  var template = document.getElementById('SignatureJudgemeMore');
  if (!template) return;

  var update = function (widget) {
    var count = widget.querySelectorAll('.jdgm-masonry-grid__masonry-item').length;
    var more = widget.nextElementSibling;
    if (!more || !more.classList.contains('signature-jdgm-more')) {
      if (count <= LIMIT) return;
      more = document.createElement('div');
      more.className = 'signature-jdgm-more';
      more.appendChild(template.content.cloneNode(true));
      more.querySelector('button').addEventListener('click', function () {
        widget.classList.add('signature-jdgm-expanded');
        more.hidden = true;
      });
      widget.after(more);
    }
    more.hidden = count <= LIMIT || widget.classList.contains('signature-jdgm-expanded');
  };

  var queued = false;
  var scan = function () {
    if (queued) return;
    queued = true;
    setTimeout(function () {
      queued = false;
      document.querySelectorAll('.jdgm-widget.jdgm-review-widget').forEach(update);
      document.querySelectorAll('.jdgm-write-review-modal').forEach(function (modal) {
        if (template.dataset.colorScheme) modal.classList.add(template.dataset.colorScheme);
      });
    }, 50);
  };

  new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
  scan();

  document.addEventListener('click', function (event) {
    if (!event.target.classList || !event.target.classList.contains('jdgm-write-review-modal__overlay')) return;
    var modal = event.target.closest('.jdgm-write-review-modal');
    var close = modal && modal.querySelector('.jdgm-write-review-modal__close-btn');
    if (close) close.click();
  });
})();
